import { useState } from "react";
import { useChat } from "../utils/chatContext";
import { SettingsModal } from "./SettingsModal";
import logoUrl from "../assets/ctsi-buffalo-logo.png";

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex items-center gap-1.5 text-[11px] font-medium text-slate-400 cursor-pointer px-2 py-1 rounded-full hover:bg-slate-100 hover:text-slate-500 transition-colors select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only peer"
      />
      <div className="w-7 h-4 bg-slate-300 peer-checked:bg-[#005bbb] rounded-full relative transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-transform after:shadow-sm peer-checked:after:translate-x-3" />
      <span>{label}</span>
    </label>
  );
}

export function Header({ onOpenReview }: { onOpenReview?: () => void }) {
  const {
    config,
    streaming,
    setStreaming,
    clearChat,
    isLoading,
    debug,
    setDebug,
  } = useChat();
  const [settingsOpen, setSettingsOpen] = useState(false);

  return (
    <>
      <header className="bg-white/85 backdrop-blur-xl border-b border-slate-200/70 px-4 py-2.5 sticky top-0 z-10">
        <div className="flex items-center justify-between max-w-3xl mx-auto">
          <div className="flex items-center gap-3">
            <img
              src={logoUrl}
              alt="CTSI Buffalo"
              className="w-9 h-9 rounded-xl shadow-sm shadow-blue-900/20"
            />
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-[15px] font-semibold text-slate-900 leading-tight tracking-tight">
                  CTSI Assistant
                </h1>
                <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200/70 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  {config ? "Live" : "Connecting"}
                </span>
              </div>
              <p className="text-[11px] text-slate-400 leading-tight mt-0.5">
                University at Buffalo · Clinical & Translational Science
                Institute
              </p>
            </div>
          </div>

          <div className="flex items-center gap-0.5">
            <div className="hidden md:flex items-center">
              <Toggle
                checked={streaming}
                onChange={setStreaming}
                label="Stream"
              />
              <Toggle checked={debug} onChange={setDebug} label="Debug" />
            </div>

            <button
              onClick={clearChat}
              disabled={isLoading}
              className="p-2 text-slate-400 hover:text-[#005bbb] hover:bg-blue-50 rounded-xl transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-400"
              title="New conversation"
            >
              <svg
                className="w-[18px] h-[18px]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={1.7}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125"
                />
              </svg>
            </button>

            {onOpenReview && (
              <button
                onClick={onOpenReview}
                className="p-2 text-slate-400 hover:text-[#005bbb] hover:bg-blue-50 rounded-xl transition-colors"
                title="Review chats"
              >
                <svg
                  className="w-[18px] h-[18px]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={1.7}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </button>
            )}

            <button
              onClick={() => setSettingsOpen(true)}
              className="p-2 text-slate-400 hover:text-[#005bbb] hover:bg-blue-50 rounded-xl transition-colors"
              title="Settings"
            >
              <svg
                className="w-[18px] h-[18px]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={1.7}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M10.343 3.94c.09-.542.56-.94 1.11-.94h1.093c.55 0 1.02.398 1.11.94l.149.894c.07.424.384.764.78.93.398.164.848.142 1.224-.045l1.387-.795a1.125 1.125 0 011.45.435l.547 1.093a1.125 1.125 0 01-.394 1.455l-.894.564c-.384.242-.597.68-.535 1.126.06.45.37.82.796.94l.934.285c.534.162.877.67.877 1.225v.866c0 .554-.343 1.063-.877 1.225l-.934.285a1.125 1.125 0 00-.796.94 1.125 1.125 0 00.535 1.126l.894.564c.453.286.61.908.394 1.455l-.547 1.093a1.125 1.125 0 01-1.45.435l-1.387-.795a1.125 1.125 0 00-1.224-.045 1.125 1.125 0 00-.78.93l-.149.894c-.09.542-.56.94-1.11.94h-1.093c-.55 0-1.02-.398-1.11-.94l-.149-.894a1.125 1.125 0 00-.78-.93 1.125 1.125 0 00-1.223.045l-1.388.795a1.125 1.125 0 01-1.45-.435l-.547-1.093a1.125 1.125 0 01.394-1.455l.894-.564c.384-.242.597-.68.535-1.126a1.125 1.125 0 00-.796-.94l-.934-.285a1.125 1.125 0 01-.877-1.225v-.866c0-.554.343-1.063.877-1.225l.934-.285c.426-.12.736-.49.796-.94.062-.446-.151-.884-.535-1.126l-.894-.564a1.125 1.125 0 01-.394-1.455l.547-1.093a1.125 1.125 0 011.45-.435l1.387.795c.376.187.827.209 1.224.045a1.125 1.125 0 00.78-.93l.149-.894z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </button>
          </div>
        </div>
      </header>
      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </>
  );
}
